import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { ArrowLeft, FileDown, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { usePublishedMoveLines } from "~/hooks/contabilidad/useDiarioSummary";
import { useJournals } from "~/hooks/contabilidad/useJournals";
import { useToast } from "~/hooks/use-toast";
import { diarioService } from "~/services/contabilidad/diarioService";
import { exportLibroDiarioToExcel } from "~/services/contabilidad/libroDiarioExportService";
import { formatCurrency } from "~/utils/helpers";

import { AccountCodeCombobox } from "./components/AccountCodeCombobox";
import { LibroDiarioTable } from "./components/LibroDiarioTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

export default function LibroDiarioPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [dateFrom, setDateFrom] = useState(() =>
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(() =>
    dayjs().endOf("month").format("YYYY-MM-DD")
  );
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [journalFilter, setJournalFilter] = useState<string>("all");
  const [accountFrom, setAccountFrom] = useState<string>("");
  const [accountTo, setAccountTo] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);

  const { data: companies = [] } = useCompanies();
  const { data: journals = [] } = useJournals();
  const { data: accounts = [] } = useAccounts();

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && companyFilter === "all") {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      }
    }
  }, [companies]);

  // Normalize the account-code range. Order of selection doesn't matter, and a
  // single selected account acts as a single-account filter (both bounds = that
  // code) rather than an open-ended "everything up to X".
  const chosenCodes = [accountFrom, accountTo].filter(Boolean).sort();
  const accountCodeFrom = chosenCodes[0] ?? "";
  const accountCodeTo = chosenCodes[chosenCodes.length - 1] ?? "";

  const filters = useMemo(
    () => ({
      dateFrom,
      dateTo,
      companyId: companyFilter === "all" ? undefined : companyFilter,
      journalId: journalFilter === "all" ? undefined : journalFilter,
      accountCodeFrom: accountCodeFrom || undefined,
      accountCodeTo: accountCodeTo || undefined,
    }),
    [
      dateFrom,
      dateTo,
      companyFilter,
      journalFilter,
      accountCodeFrom,
      accountCodeTo,
    ]
  );

  const { data: lines = [], isLoading } = usePublishedMoveLines(filters);

  // Journals for the selected company
  const filteredJournals = useMemo(() => {
    if (companyFilter === "all") return journals;
    return journals.filter((j) => j.company_id === companyFilter);
  }, [journals, companyFilter]);

  // Active accounts for the selected company (deduped by code), sorted by code.
  const accountOptions = useMemo(() => {
    const pool =
      companyFilter === "all"
        ? accounts
        : accounts.filter((a) => a.company_id === companyFilter);
    const byCode = new Map<string, (typeof accounts)[number]>();
    for (const a of pool) {
      if (!a.is_active) continue;
      if (!byCode.has(a.code)) byCode.set(a.code, a);
    }
    return Array.from(byCode.values()).sort((x, y) =>
      x.code.localeCompare(y.code)
    );
  }, [accounts, companyFilter]);

  const totals = useMemo(
    () =>
      lines.reduce(
        (acc, l) => {
          acc.debe += Number(l.debit) || 0;
          acc.haber += Number(l.credit) || 0;
          return acc;
        },
        { debe: 0, haber: 0 }
      ),
    [lines]
  );

  // Reset journal + account range when company changes
  const handleCompanyChange = (value: string) => {
    setCompanyFilter(value);
    setJournalFilter("all");
    setAccountFrom("");
    setAccountTo("");
  };

  const accountRangeLabel = useMemo(() => {
    if (!accountCodeFrom && !accountCodeTo) return undefined;
    return `${accountCodeFrom || "(inicio)"} - ${accountCodeTo || "(fin)"}`;
  }, [accountCodeFrom, accountCodeTo]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportLines = await diarioService.getPublishedMoveLines(filters);

      if (exportLines.length === 0) {
        toast({
          title: "Sin datos",
          description:
            "No hay comprobantes para exportar con los filtros seleccionados.",
          variant: "destructive",
        });
        return;
      }

      const companyName =
        companyFilter === "all"
          ? "Todas las empresas"
          : companies.find((c) => c.id === companyFilter)?.name || "";
      const journalName =
        journalFilter === "all"
          ? undefined
          : filteredJournals.find((j) => j.id === journalFilter)?.name;

      exportLibroDiarioToExcel({
        lines: exportLines,
        dateFrom,
        dateTo,
        companyName,
        journalName,
        accountRange: accountRangeLabel,
      });

      toast({
        title: "Reporte exportado",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Error exporting Libro Diario:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/contabilidad/reportes")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Libro Diario</h1>
          <p className="text-sm text-muted-foreground">
            Registro cronológico de comprobantes publicados
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-end">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ld-desde" className="text-sm font-medium">
                Desde
              </Label>
              <Input
                id="ld-desde"
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-44 rounded-xl"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ld-hasta" className="text-sm font-medium">
                Hasta
              </Label>
              <Input
                id="ld-hasta"
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-44 rounded-xl"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">Empresa</Label>
              <Select value={companyFilter} onValueChange={handleCompanyChange}>
                <SelectTrigger className="h-9 w-[200px] rounded-xl">
                  <SelectValue placeholder="Filtrar por empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">Diario</Label>
              <Select value={journalFilter} onValueChange={setJournalFilter}>
                <SelectTrigger className="h-9 w-[200px] rounded-xl">
                  <SelectValue placeholder="Filtrar por diario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los diarios</SelectItem>
                  {filteredJournals.map((journal) => (
                    <SelectItem key={journal.id} value={journal.id}>
                      {journal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">Cuenta desde</Label>
              <AccountCodeCombobox
                accounts={accountOptions}
                value={accountFrom}
                onChange={setAccountFrom}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">Cuenta hasta</Label>
              <AccountCodeCombobox
                accounts={accountOptions}
                value={accountTo}
                onChange={setAccountTo}
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Selecciona una sola cuenta para ver únicamente esa cuenta, o una en
            “Cuenta desde” y otra en “Cuenta hasta” para un rango o grupo (ej.
            1202001 a 1202014). Deja ambas en “Todas las cuentas” para incluir
            todo el plan de cuentas.
          </p>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Detalle de Comprobantes</CardTitle>
              <p className="text-sm text-muted-foreground">
                {lines.length} líneas · Debe ${formatCurrency(totals.debe)} ·
                Haber ${formatCurrency(totals.haber)}
              </p>
            </div>
            <Button onClick={handleExport} disabled={isExporting || isLoading}>
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="mr-2 h-4 w-4" />
              )}
              Exportar Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <LibroDiarioTable lines={lines} isLoading={isLoading} />
        </CardContent>
      </Card>
    </div>
  );
}
